// Health (plan step 5.4; D56): "is anything broken" without opening Northflank or Cloudflare. The
// warnings first, then each source on its own: the ingest Worker and its queue (Cloudflare),
// ClickHouse (disks, merges, parts, freshness, symbolication) and the organizations capturing
// the most against what they pay. A source not set up yet or down says so and hides nothing
// else. Refreshes every minute; the API reads each source at most once a minute.
// Console D (APS-412, admin-d.md E): a strip of each source's key number with its status word
// under it, the warnings as status lines, facts in `.kv`, ClickHouse's writes as a line chart,
// tables in `.t`.
import {
  ErrorState as ErrorLine,
  KV,
  LineChart,
  PageHead,
  Section,
  SectionHead,
  Status,
  type StatusKey,
  Strip,
} from '@apsio/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { type SystemHealth, systemQuery } from '../api/queries'
import { DataTable, NotConfigured } from '../components/kit'
import { Empty, ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { percent, shortDay } from '../lib/format'

type SourceState = { status: 'ok' | 'not_configured' | 'unavailable'; at: string | null }
type Warning = SystemHealth['warnings'][number]

const WARNING: Record<Warning, MessageId> = {
  ingest_errors: 'health.warning.ingest_errors',
  queue_lag: 'health.warning.queue_lag',
  disk: 'health.warning.disk',
  parts: 'health.warning.parts',
  stale: 'health.warning.stale',
  symbolication: 'health.warning.symbolication',
  nfse_certificate: 'health.warning.nfse_certificate',
  stripe_webhooks: 'health.warning.stripe_webhooks',
  stripe_events_unchecked: 'health.warning.stripe_events_unchecked',
  final_usage_missing: 'health.warning.final_usage_missing',
  backups: 'health.warning.backups',
  queue_backlog: 'health.warning.queue_backlog',
  worker_errors: 'health.warning.worker_errors',
  infra_budget: 'health.warning.infra_budget',
  infra_budget_unknown: 'health.warning.infra_budget_unknown',
  cost_model_gap: 'health.warning.cost_model_gap',
  edge_key_drift: 'health.warning.edge_key_drift',
  audit_log_partitions: 'health.warning.audit_log_partitions',
}

/** The warnings that speak about each source in the strip. */
const SOURCE_WARNINGS: Record<'ingest' | 'queue' | 'clickhouse' | 'symbolication', Warning[]> = {
  ingest: ['ingest_errors'],
  queue: ['queue_lag', 'queue_backlog'],
  clickhouse: ['disk', 'parts', 'stale'],
  symbolication: ['symbolication'],
}

/** A source's state as a status word: `✓ ok`, `! atenção`, `× fora`, `– não configurado`. */
const STATE: Record<'ok' | 'warn' | 'down' | 'off', [StatusKey, string, MessageId]> = {
  ok: ['ok', '✓', 'health.state.ok'],
  warn: ['miss', '!', 'health.state.warn'],
  down: ['fire', '×', 'health.state.down'],
  off: ['mute', '–', 'health.state.off'],
}

function StateWord({ state }: { state: keyof typeof STATE }) {
  const { t } = useI18n()
  const [s, glyph, word] = STATE[state]
  return <Status s={s} glyph={glyph} label={t(word)} />
}

/** A strip cell's line: the source's status word, then what it says ("! atenção · 1,2 % de erros"). */
function SourceLine({ state, children }: { state: keyof typeof STATE; children?: ReactNode }) {
  return (
    <>
      <StateWord state={state} />
      {children ? <> · {children}</> : null}
    </>
  )
}

function stateOf(section: SourceState, warnings: Warning[], mine: Warning[]) {
  if (section.status === 'not_configured') return 'off'
  if (section.status === 'unavailable') return 'down'
  return mine.some((w) => warnings.includes(w)) ? 'warn' : 'ok'
}

/** A source not set up yet or down: one line in its section; otherwise what it read. */
function Source({ section, children }: { section: SourceState; children: ReactNode }) {
  const { t } = useI18n()
  if (section.status === 'not_configured')
    return <NotConfigured missing={t('health.notConfigured')} task={t('kit.ownerTask')} />
  if (section.status === 'unavailable') return <ErrorLine message={t('health.unavailable')} />
  return <>{children}</>
}

export function HealthPage() {
  const { t, number, dateTime, date } = useI18n()
  const health = useQuery(systemQuery)
  const bytes = (n: number) =>
    number(n / 1024 ** 3, { style: 'unit', unit: 'gigabyte', maximumFractionDigits: 1 })
  const share = (part: number, whole: number) => (whole > 0 ? percent(number, part / whole) : '—')
  const money = (minor: number, currency: string) =>
    number(minor / 100, { style: 'currency', currency: currency.toUpperCase() })
  const hhmm = (at: string | number) => date(at, { hour: '2-digit', minute: '2-digit' })
  const compact = (n: number) => number(n, { notation: 'compact' })

  if (health.isPending)
    return (
      <main id="main" className="pg ops">
        <PageHead title={t('health.title')} />
        <Loading />
      </main>
    )
  if (health.isError)
    return (
      <main id="main" className="pg ops">
        <PageHead title={t('health.title')} />
        <ErrorState error={health.error} retry={() => health.refetch()} />
      </main>
    )

  const v = health.data
  const ch = v.clickhouse.data
  const ingest = v.ingest.data
  const queue = v.queue.data
  const reads = [v.ingest, v.queue, v.clickhouse, v.top_organizations].flatMap((x) =>
    x.at ? [Date.parse(x.at)] : [],
  )
  const readAt = reads.length > 0 ? Math.max(...reads) : health.dataUpdatedAt
  const series = ch?.records_per5_minutes ?? []

  return (
    <main id="main" className="pg ops">
      <PageHead
        title={t('health.title')}
        meta={`${
          v.warnings.length === 0
            ? t('health.allClear')
            : t('health.warnings', { n: v.warnings.length })
        } · ${t('health.read', { time: hhmm(readAt) })}`}
      />

      <Strip
        label={t('health.sources')}
        items={[
          {
            dt: t('health.ingestTitle'),
            dd: ingest ? number(ingest.last_hour.requests) : '—',
            small: ingest ? t('health.strip.perHour') : undefined,
            d: (
              <SourceLine state={stateOf(v.ingest, v.warnings, SOURCE_WARNINGS.ingest)}>
                {ingest
                  ? t('health.strip.errors', {
                      share: share(ingest.last_hour.errors, ingest.last_hour.requests),
                    })
                  : null}
              </SourceLine>
            ),
          },
          {
            dt: t('health.queueTitle'),
            dd: queue ? number(queue.messages) : '—',
            small: queue ? t('health.strip.waiting') : undefined,
            d: (
              <SourceLine state={stateOf(v.queue, v.warnings, SOURCE_WARNINGS.queue)}>
                {queue && queue.oldest_age_seconds !== null
                  ? t('health.strip.oldest', {
                      age: t('health.seconds', { n: queue.oldest_age_seconds }),
                    })
                  : null}
              </SourceLine>
            ),
          },
          {
            dt: t('health.clickhouseTitle'),
            dd: ch?.newest_record_at ? hhmm(ch.newest_record_at) : '—',
            d: (
              <SourceLine state={stateOf(v.clickhouse, v.warnings, SOURCE_WARNINGS.clickhouse)}>
                {ch?.newest_record_at
                  ? t('health.strip.newest', { day: shortDay(date, ch.newest_record_at) })
                  : null}
              </SourceLine>
            ),
          },
          {
            dt: t('health.symbolicationTitle'),
            dd: ch ? share(ch.occurrences24h.unsymbolicated, ch.occurrences24h.total) : '—',
            d: (
              <SourceLine state={stateOf(v.clickhouse, v.warnings, SOURCE_WARNINGS.symbolication)}>
                {ch ? t('health.strip.unsymbolicated') : null}
              </SourceLine>
            ),
          },
        ]}
      />

      {v.warnings.length > 0 ? (
        <Section
          title={t('health.warningsTitle')}
          meta={t('health.warnings', { n: v.warnings.length })}
        >
          <ul className="ops-warn">
            {v.warnings.map((w) => (
              <li key={w}>
                <Status s="miss" glyph="!" label={t('health.state.warn')} />
                <span>{t(WARNING[w])}</span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      <Section title={t('health.ingestTitle')}>
        <Source section={v.ingest}>
          {ingest ? (
            <KV
              items={[
                [t('health.requestsHour'), number(ingest.last_hour.requests)],
                [t('health.errorsHour'), share(ingest.last_hour.errors, ingest.last_hour.requests)],
                [t('health.requests5'), number(ingest.last5_minutes.requests)],
              ]}
            />
          ) : null}
        </Source>
        <SectionHead minor title={t('health.queueTitle')} />
        <Source section={v.queue}>
          {queue ? (
            <KV
              items={[
                [t('health.backlog'), number(queue.messages)],
                [
                  t('health.oldest'),
                  queue.oldest_age_seconds === null
                    ? t('common.none')
                    : t('health.seconds', { n: queue.oldest_age_seconds }),
                ],
              ]}
            />
          ) : null}
        </Source>
      </Section>

      <Section title={t('health.clickhouseTitle')}>
        <Source section={v.clickhouse}>
          {ch ? (
            <>
              <KV
                items={[
                  ...ch.disks.map(
                    (d) =>
                      [
                        t('health.disk', { name: d.name }),
                        t('health.diskValue', {
                          used: share(d.total_bytes - d.free_bytes, d.total_bytes),
                          free: bytes(d.free_bytes),
                        }),
                      ] as const,
                  ),
                  [t('health.merges'), number(ch.merges)],
                  [
                    t('health.newest'),
                    ch.newest_record_at ? dateTime(ch.newest_record_at) : t('common.none'),
                  ],
                  [
                    t('health.recordsHour'),
                    number(series.reduce((n, b) => n + b.logs + b.spans, 0)),
                  ],
                  [
                    t('health.unsymbolicated'),
                    t('health.unsymbolicatedValue', {
                      share: share(ch.occurrences24h.unsymbolicated, ch.occurrences24h.total),
                      total: ch.occurrences24h.total,
                    }),
                  ],
                ]}
              />
              {series.length > 1 ? (
                <>
                  <SectionHead minor title={t('health.recordsChart')} />
                  <div className="ops-chart">
                    <LineChart
                      label={t('health.recordsChart')}
                      name={t('health.spans')}
                      points={series.map((b) => ({ label: hhmm(b.at), value: b.spans }))}
                      lastLabel={(p) => `${t('health.spans')} ${compact(p.value ?? 0)}`}
                      more={[
                        {
                          name: t('health.logs'),
                          values: series.map((b) => b.logs),
                          end: `${t('health.logs')} ${compact(series.at(-1)?.logs ?? 0)}`,
                        },
                      ]}
                      legend
                      boxLegend
                      integer
                      fromZero
                      format={compact}
                    />
                  </div>
                </>
              ) : null}
              {ch.busiest_partitions.length > 0 ? (
                <>
                  <SectionHead minor title={t('health.partsTitle')} />
                  <DataTable label={t('health.partsTitle')} minWidth={480}>
                    <thead>
                      <tr>
                        <th scope="col" className="f">
                          {t('health.col.table')}
                        </th>
                        <th scope="col">{t('health.col.partition')}</th>
                        <th scope="col" className="num last">
                          {t('health.col.parts')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ch.busiest_partitions.map((p) => (
                        <tr key={`${p.table}-${p.partition}`}>
                          <td className="f" translate="no">
                            {p.table}
                          </td>
                          <td translate="no">{p.partition}</td>
                          <td className="num last">{number(p.parts)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                </>
              ) : null}
            </>
          ) : null}
        </Source>
      </Section>

      <Section title={t('health.topTitle')} meta={t('health.topSub')}>
        <Source section={v.top_organizations}>
          {v.top_organizations.data && v.top_organizations.data.length > 0 ? (
            <DataTable label={t('table.of', { name: t('health.topTitle') })} minWidth={560}>
              <thead>
                <tr>
                  <th scope="col" className="f">
                    {t('health.col.org')}
                  </th>
                  <th scope="col">{t('health.col.plan')}</th>
                  <th scope="col" className="num">
                    {t('health.col.sessions')}
                  </th>
                  <th scope="col" className="num last">
                    {t('health.col.mrr')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {v.top_organizations.data.map((o) => (
                  <tr key={o.org_id}>
                    <td className="f">
                      <Link
                        className="ul"
                        to="/orgs/$orgId"
                        params={{ orgId: o.org_id }}
                        translate="no"
                      >
                        {o.name}
                      </Link>
                    </td>
                    <td>{t(`plan.${o.plan}` as MessageId)}</td>
                    <td className="num">{number(o.captured_sessions)}</td>
                    <td className="num last">
                      {o.mrr.length === 0
                        ? t('common.none')
                        : o.mrr.map((m) => money(m.amount, m.currency)).join(' + ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : (
            <Empty title={t('health.noTop')} />
          )}
        </Source>
      </Section>
    </main>
  )
}
